import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import { createContext, PropsWithChildren, useContext, useRef } from 'react';
import { useHubChromeEnabled } from 'utils/featureFlags';
import { usePanelWidthRatio } from './usePanelWidthRatio';

/** Nearest ancestor with a real box (skips `display: contents` wrappers). */
function measurableParent(el: HTMLElement | null): HTMLElement | null {
  let current = el?.parentElement ?? null;
  while (current && current.getBoundingClientRect().width === 0) {
    current = current.parentElement;
  }
  return current;
}

type PanelResizeHandleProps = ReturnType<typeof usePanelWidthRatio> & {
  panelRef: React.RefObject<HTMLDivElement | null>;
};

const PanelResizeHandle = ({
  panelRef,
  ratio,
  isResizing,
  startResize,
  onHandleKeyDown,
}: PanelResizeHandleProps) => (
  // WAI-ARIA window-splitter: a focusable separator with aria-valuenow is the
  // canonical resizable-pane pattern, which jsx-a11y does not model.
  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
  <div
    role="separator"
    aria-orientation="vertical"
    aria-label="Resize panel"
    aria-valuenow={Math.round(ratio * 100)}
    aria-valuemin={50}
    aria-valuemax={75}
    tabIndex={0}
    onPointerDown={(event) =>
      startResize(event, measurableParent(panelRef.current))
    }
    onKeyDown={onHandleKeyDown}
    data-testid="map-panel-resize-handle"
    className={cn(
      'tw:absolute tw:inset-y-0 tw:right-0 tw:z-10 tw:w-1.5 tw:translate-x-1/2 tw:cursor-col-resize tw:rounded-full tw:transition-colors tw:hover:bg-primary/60 tw:focus-visible:bg-primary tw:focus-visible:outline-none',
      isResizing && 'tw:bg-primary'
    )}
  />
);

type MapSidebarContainerProps = {
  isMainSidebarVisible: boolean;
  setIsMainSidebarVisible: (visible: boolean) => void;
  /** Map hidden: the panel is the whole workspace, not the left half. */
  fullWidth?: boolean;
};

type MapSidebarCollapseContextValue = {
  isMainSidebarVisible: boolean;
  setIsMainSidebarVisible: (visible: boolean) => void;
};

const MapSidebarCollapseContext =
  createContext<MapSidebarCollapseContextValue | null>(null);

export const useMapSidebarCollapse = () => {
  return useContext(MapSidebarCollapseContext);
};

export const MapSidebarContainer = ({
  children,
  isMainSidebarVisible,
  setIsMainSidebarVisible,
  fullWidth = false,
}: PropsWithChildren<MapSidebarContainerProps>) => {
  const hubChrome = useHubChromeEnabled();
  const panelResize = usePanelWidthRatio();
  const panelRef = useRef<HTMLDivElement>(null);
  const panelWidth = fullWidth ? '100%' : `${panelResize.ratio * 100}%`;
  const resizeHandle = fullWidth ? null : (
    <PanelResizeHandle {...panelResize} panelRef={panelRef} />
  );

  const collapseValue: MapSidebarCollapseContextValue = {
    isMainSidebarVisible,
    setIsMainSidebarVisible,
  };

  if (hubChrome) {
    return (
      <MapSidebarCollapseContext.Provider value={collapseValue}>
        <Collapsible
          open={isMainSidebarVisible}
          onOpenChange={setIsMainSidebarVisible}
          className="dark tw:contents tw:text-foreground"
        >
          {isMainSidebarVisible ? (
            <div
              ref={panelRef}
              className="d-flex align-items-stretch tw:relative h-100"
              style={{ width: panelWidth, minHeight: 0 }}
            >
              <div
                className={cn(
                  'map-left-sidebar',
                  'tw:bg-background tw:text-foreground'
                )}
                style={{ backgroundColor: 'var(--background, #0a0a0a)' }}
              >
                {children}
              </div>
              {resizeHandle}
            </div>
          ) : (
            <div
              className="dark tw:flex tw:h-full tw:w-14 tw:shrink-0 tw:flex-col tw:items-center tw:border-r tw:border-border tw:bg-background tw:p-2"
              style={{ backgroundColor: 'var(--background, #0a0a0a)' }}
            >
              <CollapsibleTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Expand panel"
                  />
                }
              >
                <ChevronRight className="tw:h-4 tw:w-4" />
              </CollapsibleTrigger>
            </div>
          )}
        </Collapsible>
      </MapSidebarCollapseContext.Provider>
    );
  }

  return (
    <MapSidebarCollapseContext.Provider value={collapseValue}>
      {isMainSidebarVisible ? (
        <div
          ref={panelRef}
          className="d-flex align-items-stretch tw:relative h-100"
          style={{ width: panelWidth, minHeight: 0 }}
        >
          <div className="map-left-sidebar">{children}</div>
          {resizeHandle}
          {setIsMainSidebarVisible && (
            <StylishNewButton
              className="button--secondary map-sidebar-button"
              style={{ backgroundColor: 'var(--background-dark)' }}
              onClick={() => {
                setIsMainSidebarVisible(false);
              }}
            >
              <SharedIcon iconName="chevron_left" size="sm" />
            </StylishNewButton>
          )}
        </div>
      ) : (
        <StylishNewButton
          className="button--secondary map-sidebar-button map-sidebar-button-in"
          style={{ backgroundColor: 'var(--background-dark)' }}
          onClick={() => {
            setIsMainSidebarVisible(true);
          }}
        >
          <SharedIcon iconName="chevron_right" size="sm" />
        </StylishNewButton>
      )}
    </MapSidebarCollapseContext.Provider>
  );
};
